import { flattenSchemas, METADATA_SCHEMAS } from '@/config/metadata-schemas.config'
import type {
  FlatMetaFieldSchema,
  MetaEntityKind,
  MetaValueSchema,
  SellerType,
} from '@/config/metadata-schemas.config'

/**
 * Helpers for the metadata panel. The panel renders one self-contained field
 * per schema entry (its own input, validation, and Save button), so it only
 * needs the field list for a resource kind and the current wire value per field
 * — no JSON-schema translation or whole-form data binding.
 */

const ALL_FIELDS = flattenSchemas(METADATA_SCHEMAS.global)

/**
 * Whether a field is relevant for the given seller type (admission →
 * exhibition, events → events). Driven by schema `x-appliesTo`. A field tagged
 * `not-read` never applies: the app does not read it yet, so editing it would
 * do nothing.
 * @param {FlatMetaFieldSchema} field - the field descriptor
 * @param {SellerType} sellerType - the seller side currently being inspected
 * @returns {boolean} true when the field should be shown
 */
export const fieldAppliesToSellerType = (
  field: FlatMetaFieldSchema,
  sellerType: SellerType,
): boolean => (
  field['x-appliesTo'].includes(sellerType) && !field['x-status']?.includes('not-read')
)

/**
 * The schema fields for one entity kind, in schema order, filtered to those
 * that apply to the current seller type.
 * @param {MetaEntityKind} resourceType - the entity whose fields to list
 * @param {SellerType} sellerType - exhibition (admission) or events
 * @returns {FlatMetaFieldSchema[]} the field descriptors
 */
export const getResourceFields = (
  resourceType: MetaEntityKind,
  sellerType: SellerType,
): FlatMetaFieldSchema[] => (
  ALL_FIELDS.filter((field) => (
    field.resourceType === resourceType && fieldAppliesToSellerType(field, sellerType)
  ))
)

/**
 * Whether this field is required for the current seller type before go-live.
 * Uses `x-requiredFor` (must include the seller type) — not merely "has any
 * required-for entry".
 * @param {FlatMetaFieldSchema} field - the field descriptor
 * @param {SellerType} sellerType - the seller side currently being inspected
 * @returns {boolean} true when the field is required for this seller type
 */
export const isFieldRequired = (
  field: FlatMetaFieldSchema,
  sellerType: SellerType,
): boolean => (
  Boolean(field['x-requiredFor']?.includes(sellerType))
)

/**
 * Schema fields for one entity kind with required fields first (stable order
 * within required and optional groups). Already filtered by seller type.
 * @param {MetaEntityKind} resourceType - the entity whose fields to list
 * @param {SellerType} sellerType - exhibition (admission) or events
 * @returns {FlatMetaFieldSchema[]} required fields, then optional
 */
export const getResourceFieldsRequiredFirst = (
  resourceType: MetaEntityKind,
  sellerType: SellerType,
): FlatMetaFieldSchema[] => {
  const fields = getResourceFields(resourceType, sellerType)
  return [
    ...fields.filter((field) => isFieldRequired(field, sellerType)),
    ...fields.filter((field) => !isFieldRequired(field, sellerType)),
  ]
}

/**
 * The current wire values for one resource instance, keyed by schema key. Used
 * to seed each field's input; a field with no row is simply absent (unset).
 *
 * @param {MetadataManagementTool.Metadata[]} values - all value rows in the snapshot
 * @param {MetaEntityKind} resourceType - the entity kind to collect
 * @param {string} resourceId - the specific instance id
 * @returns {Record<string, string>} key → wire value
 */
export const getResourceValues = (
  values: MetadataManagementTool.Metadata[],
  resourceType: MetaEntityKind,
  resourceId: string,
): Record<string, string> => {
  const byKey: Record<string, string> = {}
  values
    .filter((value) => value.resourceType === resourceType && value.resourceId === resourceId)
    .forEach((value) => {
      byKey[value.key] = value.value
    })
  return byKey
}

/**
 * Required fields for one resource instance that have no (or blank) value,
 * for the current seller type only.
 * @param {MetaEntityKind} resourceType - the entity kind
 * @param {string} resourceId - the specific instance id
 * @param {MetadataManagementTool.Metadata[]} values - all value rows in the snapshot
 * @param {SellerType} sellerType - exhibition (admission) or events
 * @returns {FlatMetaFieldSchema[]} missing required field descriptors
 */
export const getMissingRequiredFieldsForResource = (
  resourceType: MetaEntityKind,
  resourceId: string,
  values: MetadataManagementTool.Metadata[],
  sellerType: SellerType,
): FlatMetaFieldSchema[] => {
  const currentValues = getResourceValues(values, resourceType, resourceId)
  return getResourceFields(resourceType, sellerType).filter((field) => (
    isFieldRequired(field, sellerType) && !currentValues[field.key]?.trim()
  ))
}

/** One line of the panel's missing-required summary (grouped by resource). */
export type MissingRequiredSummary = {
  sectionTitle: string
  resourceType: MetaEntityKind
  resourceId: string
  fieldKeys: string[]
}

/**
 * Build succinct missing-required rows for the panel summary. Only sections
 * with at least one missing required field are included.
 * @param {{ title: string, resourceType: MetaEntityKind, resourceId: string }[]} sections - panel sections in display order
 * @param {MetadataManagementTool.Metadata[]} values - all value rows in the snapshot
 * @param {SellerType} sellerType - exhibition (admission) or events
 * @returns {MissingRequiredSummary[]} one entry per section that still needs required fields
 */
export const getMissingRequiredSummary = (
  sections: Array<{ title: string, resourceType: MetaEntityKind, resourceId: string }>,
  values: MetadataManagementTool.Metadata[],
  sellerType: SellerType,
): MissingRequiredSummary[] => (
  sections.flatMap((section) => {
    const missing = getMissingRequiredFieldsForResource(
      section.resourceType,
      section.resourceId,
      values,
      sellerType,
    )
    if (!missing.length) {
      return []
    }
    return [{
      sectionTitle: section.title,
      resourceType: section.resourceType,
      resourceId: section.resourceId,
      fieldKeys: missing.map((field) => field.key),
    }]
  })
)

/**
 * Render an example value as help text: strings as-is, everything else (blobs,
 * numbers, booleans) as JSON.
 * @param {unknown} example - the schema example
 * @returns {string} display string
 */
export const formatMetaExample = (example: unknown): string => (
  typeof example === 'string' ? example : JSON.stringify(example)
)

// ---------------------------------------------------------------------------
// Structured blob editors (slider / pricing / exhibitionInformation, …)
// Driven by the field's own MetaValueSchema so one UI covers array-of-objects
// and object-with-sections shapes without per-field components.
// ---------------------------------------------------------------------------

/** One row in an array-of-objects blob (slider item, pricing section, …) */
export type BlobObjectRow = Record<string, string | boolean | number>

/** Narrow arbitrary parsed JSON to a plain object, discarding arrays/primitives. */
const asPlainObject = (raw: unknown): Record<string, unknown> => (
  raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, unknown> : {}
)

/**
 * Coerce an arbitrary JSON value into the editor scalar for a schema prop:
 * booleans stay boolean (default false), strings/numbers pass through for the
 * input to hold as text, anything else becomes ''.
 */
const coerceScalar = (value: unknown, prop: MetaValueSchema): string | boolean | number => {
  if (prop.type === 'boolean') {
    return typeof value === 'boolean' ? value : false
  }
  if (prop.type === 'number') {
    return typeof value === 'number' || typeof value === 'string' ? value : ''
  }
  return typeof value === 'string' ? value : ''
}

/**
 * Whether this schema can be edited with the structured blob UI (one level of
 * object properties, optionally containing array-of-object lists). Deeper or
 * unmodeled blobs fall back to the raw JSON textarea.
 * @param {MetaValueSchema} schema - field schema (type + properties/items)
 * @returns {boolean} true when MetadataBlobEditor can render it
 */
export const isStructuredBlobSchema = (schema: MetaValueSchema): boolean => {
  const isScalar = (prop: MetaValueSchema): boolean => (
    prop.type === 'string' || prop.type === 'number' || prop.type === 'boolean'
  )

  if (schema.type === 'array' && schema.items?.type === 'object' && schema.items.properties) {
    return Object.values(schema.items.properties).every(isScalar)
  }

  if (schema.type === 'object' && schema.properties) {
    return Object.values(schema.properties).every((prop) => (
      isScalar(prop)
      || (
        prop.type === 'array'
        && prop.items?.type === 'object'
        && prop.items.properties !== undefined
        && Object.values(prop.items.properties).every(isScalar)
      )
    ))
  }

  return false
}

const emptyScalar = (prop: MetaValueSchema): string | boolean => (
  prop.type === 'boolean' ? false : ''
)

/**
 * Build a blank object row matching the given property map (scalars default to
 * '' / false; nested arrays default to []).
 * @param {Readonly<Record<string, MetaValueSchema>>} properties - object shape
 * @returns {BlobObjectRow} empty row
 */
export const createEmptyObjectRow = (
  properties: Readonly<Record<string, MetaValueSchema>>,
): BlobObjectRow => {
  const row: BlobObjectRow = {}
  Object.entries(properties).forEach(([key, prop]) => {
    if (prop.type === 'array') {
      // nested arrays are handled at the parent object level, not inside a row
      return
    }
    row[key] = emptyScalar(prop)
  })
  return row
}

/**
 * Normalize an unknown value into a row that only has the schema's scalar keys.
 * @param {unknown} raw - parsed JSON object (or junk)
 * @param {Readonly<Record<string, MetaValueSchema>>} properties - expected shape
 * @returns {BlobObjectRow} coerced row
 */
export const normalizeObjectRow = (
  raw: unknown,
  properties: Readonly<Record<string, MetaValueSchema>>,
): BlobObjectRow => {
  const source = asPlainObject(raw)

  const row: BlobObjectRow = {}
  Object.entries(properties).forEach(([key, prop]) => {
    if (prop.type === 'array' || prop.type === 'object') {
      return
    }
    row[key] = coerceScalar(source[key], prop)
  })
  return row
}

/**
 * Normalize a full blob value (array of objects, or object with scalar + list
 * properties) from arbitrary parsed JSON into editor-friendly shape.
 * @param {unknown} raw - JSON.parse result (or null)
 * @param {MetaValueSchema} schema - field schema
 * @returns {unknown} array or plain object ready for the structured editor
 */
export const normalizeStructuredValue = (raw: unknown, schema: MetaValueSchema): unknown => {
  if (schema.type === 'array' && schema.items?.properties) {
    const items = Array.isArray(raw) ? raw : []
    return items.map((item) => normalizeObjectRow(item, schema.items!.properties!))
  }

  if (schema.type === 'object' && schema.properties) {
    const source = asPlainObject(raw)
    const result: Record<string, unknown> = {}

    Object.entries(schema.properties).forEach(([key, prop]) => {
      if (prop.type === 'array' && prop.items?.properties) {
        const list = Array.isArray(source[key]) ? source[key] as unknown[] : []
        result[key] = list.map((item) => normalizeObjectRow(item, prop.items!.properties!))
      } else if (prop.type === 'boolean' || prop.type === 'number' || prop.type === 'string') {
        result[key] = coerceScalar(source[key], prop)
      }
    })

    return result
  }

  return raw
}

/**
 * Whether a structured value is "empty" for the delete-on-save path: no rows
 * for arrays, and only blank scalars + empty lists for objects.
 * @param {unknown} value - editor value
 * @param {MetaValueSchema} schema - field schema
 * @returns {boolean} true when serialize should emit ''
 */
export const isEmptyStructuredValue = (value: unknown, schema: MetaValueSchema): boolean => {
  if (schema.type === 'array') {
    return !Array.isArray(value) || value.length === 0
  }

  if (schema.type === 'object' && schema.properties) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return true
    }
    const obj = value as Record<string, unknown>
    return Object.entries(schema.properties).every(([key, prop]) => {
      if (prop.type === 'array') {
        return !Array.isArray(obj[key]) || (obj[key] as unknown[]).length === 0
      }
      if (prop.type === 'boolean') {
        return obj[key] === false || obj[key] === undefined
      }
      return obj[key] === '' || obj[key] === undefined || obj[key] === null
    })
  }

  return false
}

/**
 * Strip empty optional scalars before write so optional enums (e.g. specialItem)
 * are omitted rather than sent as '' (which fails enum validation).
 * @param {BlobObjectRow} row - editor row
 * @param {Readonly<Record<string, MetaValueSchema>>} properties - item shape
 * @param {readonly string[] | undefined} required - required keys
 * @returns {Record<string, unknown>} JSON-ready object
 */
export const compactObjectRow = (
  row: BlobObjectRow,
  properties: Readonly<Record<string, MetaValueSchema>>,
  required?: readonly string[],
): Record<string, unknown> => {
  const requiredSet = new Set(required ?? [])
  const out: Record<string, unknown> = {}

  Object.entries(properties).forEach(([key, prop]) => {
    if (prop.type === 'array' || prop.type === 'object') {
      return
    }
    const value = row[key]
    if (prop.type === 'boolean') {
      out[key] = Boolean(value)
      return
    }
    if (value === '' || value === undefined) {
      if (requiredSet.has(key)) {
        out[key] = prop.type === 'number' ? 0 : ''
      }
      return
    }
    if (prop.type === 'number' && typeof value === 'string') {
      const asNumber = Number(value)
      out[key] = value === '' || Number.isNaN(asNumber) ? value : asNumber
      return
    }
    out[key] = value
  })

  return out
}

/**
 * Parse a Ticketure wire string into a structured editor value. Blank / invalid
 * JSON yields an empty structure; field-level parseMetaValue still reports errors.
 * @param {string} raw - wire string
 * @param {MetaValueSchema} schema - field schema
 * @returns {unknown} editor value
 */
export const parseStructuredWireValue = (raw: string, schema: MetaValueSchema): unknown => {
  if (!raw.trim()) {
    return normalizeStructuredValue(schema.type === 'array' ? [] : {}, schema)
  }

  try {
    return normalizeStructuredValue(JSON.parse(raw), schema)
  } catch {
    return normalizeStructuredValue(schema.type === 'array' ? [] : {}, schema)
  }
}

/**
 * Serialize a structured editor value to the wire string. Empty structures
 * become '' so Save deletes the meta key.
 * @param {unknown} value - editor value
 * @param {MetaValueSchema} schema - field schema
 * @returns {string} JSON wire string or ''
 */
export const serializeStructuredWireValue = (value: unknown, schema: MetaValueSchema): string => {
  if (isEmptyStructuredValue(value, schema)) {
    return ''
  }

  if (schema.type === 'array' && schema.items?.properties) {
    /* istanbul ignore next -- guarded by isEmptyStructuredValue above */
    const rows = Array.isArray(value) ? value as BlobObjectRow[] : []
    return JSON.stringify(rows.map((row) => (
      compactObjectRow(row, schema.items!.properties!, schema.items!.required)
    )))
  }

  if (schema.type === 'object' && schema.properties) {
    const obj = asPlainObject(value)
    const out: Record<string, unknown> = {}

    Object.entries(schema.properties).forEach(([key, prop]) => {
      if (prop.type === 'array' && prop.items?.properties) {
        const list = Array.isArray(obj[key]) ? obj[key] as BlobObjectRow[] : []
        out[key] = list.map((row) => (
          compactObjectRow(row, prop.items!.properties!, prop.items!.required)
        ))
      } else if (prop.type === 'boolean') {
        out[key] = Boolean(obj[key])
      } else if (prop.type === 'number') {
        const raw = obj[key]
        if (typeof raw === 'number') {
          out[key] = raw
        } else if (typeof raw === 'string' && raw !== '' && !Number.isNaN(Number(raw))) {
          out[key] = Number(raw)
        } else {
          out[key] = raw ?? ''
        }
      } else {
        out[key] = typeof obj[key] === 'string' ? obj[key] : ''
      }
    })

    return JSON.stringify(out)
  }

  return JSON.stringify(value)
}
